import React, { useEffect, useState, useRef } from 'react';
import { getCategories } from '../services/categoryService';
import { useNavigate, useLocation } from 'react-router-dom';

const CategorySlider = ({ onCategoryClick }) => {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    getCategories().then(res => {
      setCategories(res.data.filter(c => c.isActive));
    });
  }, []);

  const checkScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, [categories]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  // Drag to scroll functionality
  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartX(e.type === 'touchstart' ? e.touches[0].pageX : e.pageX);
    setScrollLeft(trackRef.current.scrollLeft);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.type === 'touchmove' ? e.touches[0].pageX : e.pageX;
    const walk = (x - startX) * 1.5;
    trackRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  if (!categories.length) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .category-section {
          width: 100%;
          padding: 60px 0 70px;
          background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
          font-family: 'Inter', sans-serif;
          position: relative;
          overflow: hidden;
        }

        /* Decorative background elements */
        .category-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .category-section::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -10%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }

        .category-header {
          text-align: center;
          // margin-bottom: 48px;
          padding: 0 20px;
          position: relative;
          z-index: 2;
        }

        .category-badge {
          display: inline-block;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: white;
          font-size: 13px;
          font-weight: 700;
          padding: 6px 20px;
          border-radius: 100px;
          // margin-bottom: 20px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
          animation: fadeInDown 0.6s ease-out;
        }

        .category-title {
          font-size: clamp(20px, 3vw, 32px);
          font-weight: 800;
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          // margin: 0 0 16px;
          letter-spacing: -0.02em;
          animation: fadeInUp 0.6s ease-out 0.1s both;
        }

        .category-subtitle {
          font-size: clamp(15px, 2.5vw, 18px);
          color: #64748b;
          font-weight: 400;
          margin: 0;
          max-width: 550px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.5;
          animation: fadeInUp 0.6s ease-out 0.2s both;
        }

        .category-wrapper {
          position: relative;
          padding: 0 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .category-track {
          display: flex;
          gap: 24px;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          padding: 12px 12px 28px;
          scrollbar-width: thin;
          scrollbar-color: #6366f1 #e2e8f0;
          cursor: grab;
          user-select: none;
        }

        .category-track:active {
          cursor: grabbing;
        }

        .category-track::-webkit-scrollbar {
          height: 6px;
        }

        .category-track::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 10px;
        }

        .category-track::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 10px;
        }

        .category-card {
          flex: 0 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 18px;
          cursor: pointer;
          scroll-snap-align: start;
          width: 150px;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          outline: none;
          background: none;
          border: none;
          padding: 0;
          position: relative;
        }

        .category-card:hover {
          transform: translateY(-10px);
        }

        .category-card:active {
          transform: translateY(-5px) scale(0.98);
        }

        .category-icon-wrapper {
          position: relative;
        }

        .category-icon {
          width: 130px;
          height: 130px;
          border-radius: 9%;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          position: relative;
          box-shadow: 0 15px 35px -10px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(99, 102, 241, 0.1);
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          flex-shrink: 0;
        }

        .category-card:hover .category-icon {
          box-shadow: 0 25px 45px -12px rgba(99, 102, 241, 0.3), 0 0 0 2px rgba(99, 102, 241, 0.2);
          transform: scale(1.05);
        }

        /* Animated gradient border */
        .category-icon::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7);
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.4s ease;
          z-index: -1;
        }

        .category-card:hover .category-icon::before {
          opacity: 1;
        }

        .category-icon::after {
          content: '';
          position: absolute;
          top: 15%;
          left: 20%;
          width: 30%;
          height: 25%;
          background: radial-gradient(ellipse, rgba(255,255,255,0.9) 0%, transparent 70%);
          border-radius: 50%;
          z-index: 2;
          pointer-events: none;
        }

        .category-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          position: relative;
          z-index: 1;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          -webkit-user-drag: none;
          pointer-events: none;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.05));
        }

        .category-card:hover .category-icon img {
          transform: scale(1.12);
          filter: drop-shadow(0 4px 8px rgba(99, 102, 241, 0.2));
        }

        .category-name {
          font-size: 15px;
          font-weight: 700;
          color: #1e293b;
          text-align: center;
          line-height: 1.4;
          max-width: 130px;
          transition: all 0.3s ease;
          position: relative;
        }

        .category-card:hover .category-name {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          transform: scale(1.05);
        }

        /* Count badge */
        .category-count {
          position: absolute;
          bottom: -8px;
          right: -8px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          font-size: 10px;
          font-weight: 700;
          padding: 4px 8px;
          border-radius: 20px;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
          opacity: 0;
          transform: scale(0.8);
          transition: all 0.3s ease;
          pointer-events: none;
          font-family: monospace;
        }

        .category-card:hover .category-count {
          opacity: 1;
          transform: scale(1);
        }

        /* Navigation Buttons */
        .category-nav {
          position: absolute;
          top: 45%;
          transform: translateY(-50%);
          z-index: 20;
          border: none;
          cursor: pointer;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
          color: #475569;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          outline: none;
          padding: 0;
          backdrop-filter: blur(4px);
        }

        .category-nav:hover {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          box-shadow: 0 12px 28px rgba(99, 102, 241, 0.35);
          transform: translateY(-50%) scale(1.1);
        }

        .category-nav:active {
          transform: translateY(-50%) scale(0.95);
        }

        .category-nav.left {
          left: -10px;
        }

        .category-nav.right {
          right: -10px;
        }

        .category-nav.hidden {
          opacity: 0;
          pointer-events: none;
          transform: translateY(-50%) scale(0.9);
        }

        .category-nav svg {
          width: 22px;
          height: 22px;
          stroke: currentColor;
          stroke-width: 2.2;
          fill: none;
          stroke-linecap: round;
          stroke-linejoin: round;
          transition: transform 0.2s ease;
        }

        .category-nav:hover svg {
          transform: scale(1.1);
        }

        /* Animations */
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .category-section {
            padding: 5px 0 60px;
          }
          .category-card {
            width: 130px;
          }
          .category-icon {
            width: 110px;
            height: 110px;
          }
          .category-name {
            font-size: 14px;
          }
        }

        @media (max-width: 768px) {
          .category-section {
            padding: 40px 0 50px;
          }
          .category-header {
            margin-bottom: 32px;
          }
          .category-badge {
            font-size: 11px;
            padding: 5px 16px;
            margin-bottom: 2spx;
          }
          .category-card {
            width: 110px;
            gap: 14px;
          }
          .category-icon {
            width: 90px;
            height: 90px;
          }
          .category-name {
            font-size: 12px;
            max-width: 100px;
          }
          .category-nav {
            display: none;
          }
          .category-track {
            gap: 16px;
            padding: 8px 12px 20px;
          }
          .category-wrapper {
            padding: 0 16px;
          }
        }

        @media (max-width: 640px) {
          .category-section {
            padding: 6px 0 0;
          }
          .category-card {
            width: 95px;
            gap: 12px;
          }
          .category-icon {
            width: 75px;
            height: 75px;
          }
          .category-name {
            font-size: 11px;
            max-width: 85px;
          }
          .category-track {
            gap: 14px;
          }
        }

        @media (max-width: 480px) {
          .category-card {
            width: 85px;
          }
          .category-icon {
            width: 65px;
            height: 65px;
          }
          .category-name {
            font-size: 10px;
            max-width: 75px;
          }
          .category-track {
            gap: 0px;
          }
          .category-header {
            margin-bottom: 0px;
          }
          .category-title {
            margin-bottom: -2px;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) {
          .category-card:hover {
            transform: none;
          }
          .category-card:hover .category-icon {
            transform: none;
          }
          .category-card:hover .category-name {
            background: none;
            -webkit-text-fill-color: #1e293b;
          }
        }

        /* Loading skeleton animation */
        .category-card {
          animation: cardFadeIn 0.5s ease-out backwards;
        }

        @keyframes cardFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Stagger animation for cards */
        .category-card:nth-child(1) { animation-delay: 0.05s; }
        .category-card:nth-child(2) { animation-delay: 0.1s; }
        .category-card:nth-child(3) { animation-delay: 0.15s; }
        .category-card:nth-child(4) { animation-delay: 0.2s; }
        .category-card:nth-child(5) { animation-delay: 0.25s; }
        .category-card:nth-child(6) { animation-delay: 0.3s; }
        .category-card:nth-child(7) { animation-delay: 0.35s; }
        .category-card:nth-child(8) { animation-delay: 0.4s; }
        .category-card:nth-child(9) { animation-delay: 0.45s; }
        .category-card:nth-child(10) { animation-delay: 0.5s; }
      `}</style>

      <section className="category-section">
        <div className="category-header">
          <span className="category-badge">✨ Shop by Category</span>
          <h2 className="category-title">Browse Categories</h2>
          <p className="category-subtitle">
            Discover products tailored to your needs
          </p>
        </div>

        <div className="category-wrapper">
          <button
            className={`category-nav left ${!canScrollLeft ? 'hidden' : ''}`}
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
          >
            <svg viewBox="0 0 24 24">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div
            className="category-track"
            ref={trackRef}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            {categories.map((cat, idx) => (
              <button
                key={cat._id ?? cat.id ?? idx}
                className="category-card"
                aria-label={cat.name}
                onClick={() => {
                  if (onCategoryClick) {
                    onCategoryClick(cat);
                  } else {
                    navigate(`/dashboard?page=my-shop&category=${cat._id || cat.id}`);
                  }
                }}
              >
                <div className="category-icon-wrapper">
                  <div className="category-icon">
                    <img src={cat.image} alt={cat.name} loading="lazy" />
                  </div>
                  {cat.productCount && (
                    <span className="category-count">{cat.productCount}+</span>
                  )}
                </div>
                <span className="category-name">{cat.name}</span>
              </button>
            ))}
          </div>

          <button
            className={`category-nav right ${!canScrollRight ? 'hidden' : ''}`}
            onClick={() => scroll(1)}
            aria-label="Scroll right"
          >
            <svg viewBox="0 0 24 24">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </section>
    </>
  );
};

export default CategorySlider;